import { enumRecord } from 'aos-helpers/src/helpers/Enum'
import { TFunction } from 'i18next'
import { uniq, uniqBy } from 'lodash'

import { InspectionItemPayload } from '../payload/InspectionTaskFormPayload'
import { InspectionMetadataType } from '../TaskMetadata'
import { InspectionGroup, translateInspectionGroup } from './InspectionGroup'
import { InspectionType, translateInspectionTypeName } from './InspectionType'
import { OTHER_CODE, sortWithOtherLast } from './sortWithOtherLast'

export interface InspectionConfig {
    group: InspectionGroup
    items: InspectionItemPayload[]
    selectedItems: InspectionItemPayload[]
}

export type InspectionTypeGroup = Record<InspectionGroup, InspectionConfig>

export const inspectionTypeToGroups = (
    types: InspectionMetadataType[],
    selectedInspections: InspectionItemPayload[],
): InspectionTypeGroup => {
    const selectedIds = new Set(selectedInspections.map(i => i.inspectionType.id))
    const selectionForInspection = (group: InspectionGroup): InspectionConfig => {
        const allOfGroup: InspectionItemPayload[] = types
            .filter(i => i.inspectionGroup === group)
            .map(type => ({
                inspectionType: {
                    id: type.id,
                    name: type.name,
                    code: type.code,
                    period: type.period,
                    inspectionGroup: type.inspectionGroup,
                    taskEndTime: type.taskEndTime,
                },
            }))

        return {
            items: allOfGroup,
            group,
            selectedItems: allOfGroup.filter(i => selectedIds.has(i.inspectionType.id)),
        }
    }

    return enumRecord(InspectionGroup, selectionForInspection)
}

export const diffSelection = (
    selectedInspections: InspectionItemPayload[],
    currentPayload: InspectionItemPayload[],
    inspectionGroup: InspectionGroup,
): InspectionItemPayload[] => {
    const selectedIds = new Set(selectedInspections.map(i => i.inspectionType.id))
    const keptPayload = currentPayload.filter(
        i =>
            i.inspectionType.inspectionGroup !== inspectionGroup ||
            selectedIds.has(i.inspectionType.id),
    )
    return uniqBy(keptPayload.concat(selectedInspections), i => i.inspectionType.id)
}

export const sortInspectionGroups = (groups: InspectionGroup[], translate: TFunction) =>
    sortWithOtherLast<InspectionGroup>(
        groups,
        translateInspectionGroup(translate),
        g => g === InspectionGroup.Other,
    )

export const sortInspectionItems = <T extends Pick<InspectionType, 'code' | 'name'>>(
    items: T[],
    translate: TFunction,
): T[] =>
    sortWithOtherLast<T>(items, translateInspectionTypeName(translate), i => i.code === OTHER_CODE)

export const sortInspectionTypes = (
    types: InspectionMetadataType[],
    allowedGroups: string[],
    translate: TFunction,
): InspectionMetadataType[] => {
    const groups = uniq(types.map(t => t.inspectionGroup)).filter(g => allowedGroups.includes(g))
    return sortInspectionGroups(groups, translate).flatMap(group =>
        sortInspectionItems(
            types.filter(t => t.inspectionGroup === group),
            translate,
        ),
    )
}
